import { isInEnum } from '@caresend/utils';
import { computed } from 'vue';
import { SchedulingFlowRouteName } from '../../../../router/routes/schedulingFlow/model.js';
import { getTextWithDryRun } from '../helpers/index.js';

const getConfirmTextToVisitOverview = ({ store }) => {
    const { isBrickAndMortarVisit } = store.state.schedulingFlow.localState;
    const isAppointmentOnlyVariant = store
        .getters['schedulingFlow/getIsAppointmentOnlyVariant'];
    if (isAppointmentOnlyVariant)
        return 'Next'; // From time step
    if (isBrickAndMortarVisit)
        return 'Finalize'; // From walk in date step
    // The button will be hidden for home visits, and the time slot buttons
    // act as a next button.
    return 'Next';
};
const getConfirmTextToReview = (headingParams) => {
    const { store } = headingParams;
    const isAppointmentOnlyVariant = store
        .getters['schedulingFlow/getIsAppointmentOnlyVariant'];
    if (isAppointmentOnlyVariant) {
        return getTextWithDryRun('Request appointment', headingParams);
    }
    const isRescheduling = computed(() => { var _a; return (_a = store.state.schedulingFlow.localState.isRescheduling) !== null && _a !== void 0 ? _a : false; });
    const isNurseRescheduling = computed(() => { var _a; return (_a = store.state.schedulingFlow.isNurseRescheduling) !== null && _a !== void 0 ? _a : false; });
    const nextButtonText = isRescheduling.value && !isNurseRescheduling.value
        ? 'Confirm & reschedule'
        : 'Confirm';
    return getTextWithDryRun(nextButtonText, headingParams);
};
const confirmTextGettersByNextStepRouteName = {
    [SchedulingFlowRouteName.SCHEDULING_FLOW_TIME]: () => 'View options',
    [SchedulingFlowRouteName.SCHEDULING_FLOW_WALK_IN_DATE]: () => 'Select date',
    [SchedulingFlowRouteName.SCHEDULING_FLOW_VISIT_OVERVIEW]: getConfirmTextToVisitOverview,
    [SchedulingFlowRouteName.SCHEDULING_FLOW_REVIEW]: getConfirmTextToReview,
};
const hideArrowByNextStepRouteName = {
    [SchedulingFlowRouteName.SCHEDULING_FLOW_REVIEW]: true,
};
const hideNextButtonGettersByNextStepRouteName = {
    [SchedulingFlowRouteName.SCHEDULING_FLOW_WALK_IN_DATE]: ({ store }) => {
        const { selectedDrawPlaceID } = store.state.schedulingFlow;
        // Next button `Select date` to walk-in date step is hidden until a lab is
        // selected.
        return !selectedDrawPlaceID;
    },
    [SchedulingFlowRouteName.SCHEDULING_FLOW_VISIT_OVERVIEW]: ({ store }) => {
        const { isBrickAndMortarVisit } = store.state.schedulingFlow.localState;
        // Next button to visit overview step is hidden for home visits, as the
        // time slots act as a next button. For brick and mortar visits, the button
        // shows as `Finalize` from the walk-in date step, or `Next` from the
        // lab selection ("time") step in the appointment-only variant.
        return !isBrickAndMortarVisit;
    },
    [SchedulingFlowRouteName.SCHEDULING_FLOW_REVIEW]: ({ store }) => {
        var _a, _b;
        /**
         * When there is a price on a visit, the visit overview step will show
         * a payment form which also acts as a next button.
         *
         * We are not currently supporting pricing for visits - testing will be
         * required if we re-implement pricing.
         */
        const { selectedTimeSlot } = store.state.schedulingFlow;
        return ((_b = (_a = selectedTimeSlot === null || selectedTimeSlot === void 0 ? void 0 : selectedTimeSlot.priceInfo) === null || _a === void 0 ? void 0 : _a.visitPrice) !== null && _b !== void 0 ? _b : 0) > 0;
    },
};
const getSchedulingFlowRouteName = (location) => {
    if (!location || typeof location === 'string' || !location.name)
        return undefined;
    if (isInEnum(location.name, SchedulingFlowRouteName)) {
        return location.name;
    }
    return undefined;
};
const getConfirmValuesForLastStep = (headingParams) => {
    const { store } = headingParams;
    // The visit is not actually finalized in the appointment-only variant,
    // so we do not prompt the user to navigate to the visit details page.
    const isAppointmentOnlyVariant = store
        .getters['schedulingFlow/getIsAppointmentOnlyVariant'];
    return {
        confirmText: 'See details',
        hideConfirmButton: isAppointmentOnlyVariant,
    };
};
/**
 * Gets info related to the confirm (next) button based on the **next** step
 * route name or lack thereof (final step). Includes:
 *
 * - Button text (If no match is found, "Next" is returned)
 * - Whether to display the arrow icon
 * - Whether to hide the button entirely
 */
const getSchedulingFlowConfirmButtonInfo = (headingParams, confirmToLocation) => {
    var _a, _b, _c, _d;
    const nextStepRouteName = getSchedulingFlowRouteName(confirmToLocation);
    if (!nextStepRouteName) {
        return getConfirmValuesForLastStep(headingParams);
    }
    const confirmText = (_b = (_a = confirmTextGettersByNextStepRouteName[nextStepRouteName]) === null || _a === void 0 ? void 0 : _a.call(confirmTextGettersByNextStepRouteName, headingParams)) !== null && _b !== void 0 ? _b : 'Next';
    const hideArrow = hideArrowByNextStepRouteName[nextStepRouteName];
    const hideNextButton = (_d = (_c = hideNextButtonGettersByNextStepRouteName[nextStepRouteName]) === null || _c === void 0 ? void 0 : _c.call(hideNextButtonGettersByNextStepRouteName, headingParams)) !== null && _d !== void 0 ? _d : false;
    return {
        confirmIcon: hideArrow ? undefined : 'arrow-right',
        confirmText,
        hideConfirmButton: hideNextButton,
    };
};

export { getSchedulingFlowConfirmButtonInfo };
